import api from './api';
import type { PaginationInfo } from '../types/api.types';
import type { ApiResponse } from '@/types/api.types';
import type { AuditAction, AuditResourceType, AuditResult } from '@/types/enums.generated';

/**
 * Audit log client — GET /api/v1/audit-logs (A-04, plan P-5).
 *
 * The backend has served this endpoint (admin / sub_admin, RBAC-scoped) for
 * months; the console never called it. Vocabulary the backend actually
 * writes today: actions upload / download / restore / backup_execute, on
 * resource types file / backup_job (D-09 tracks the categories still missing).
 */

// The action / resource vocabularies are generated from the backend
// (internal/audit-logs/actions.go, ADR-043 G-2).
export type { AuditAction, AuditResult } from '@/types/enums.generated';

export interface AuditLogEntry {
  id: string;
  user_id: string;
  /** Resolved by the server (QA #98); null when the user was removed. */
  username?: string | null;
  /**
   * The actor's owner group when the row was written (QA #98). Rows from
   * before that, or by a user in no group, carry none → shown as 「—」.
   */
  group_id?: string | null;
  group_name?: string | null;
  /**
   * failure = a write the server refused or failed (QA #99); details then
   * carry status / error_code / error_message / error_fields.
   */
  result?: AuditResult;
  action: AuditAction | string;
  resource_type: 'file' | 'backup_job' | string;
  resource_id?: string;
  ip_address: string;
  user_agent?: string;
  details?: Record<string, unknown>;
  timestamp: string;
}

export interface ListAuditLogsResponse {
  data: AuditLogEntry[];
  pagination: PaginationInfo;
}

export interface ListAuditLogsParams {
  action?: string;
  user_id?: string;
  /** YYYY-MM-DD, inclusive, a calendar day in `tz` */
  start_date?: string;
  /** YYYY-MM-DD, inclusive, a calendar day in `tz` */
  end_date?: string;
  /**
   * IANA zone the two days are expressed in. Send the browser's zone (QA #59:
   * without it the backend reads the days as UTC, so a UTC+8 viewer loses
   * 00:00–07:59 of the start day).
   */
  tz?: string;
  /**
   * Source IP: exact match or prefix (QA #78 — `10.0.` finds the whole
   * range). The backend trims spaces and treats `%` / `_` literally.
   */
  ip?: string;
  /** Only rows recorded under this group; narrows within the caller's scope. */
  group_id?: string;
  result?: AuditResult;
  page?: number;
  /** backend caps at 100 */
  limit?: number;
}

// Vocabulary comes from the backend (internal/audit-logs/actions.go via the
// ADR-043 codegen); every value has a write site (explicit LogAction or the
// AuditTrail middleware, SRS FR-601). A new action fails to compile here
// until it gets a label.
const AUDIT_ACTION_LABEL_MAP: Record<AuditAction, string> = {
  login: '登入',
  logout: '登出',
  password_change: '變更密碼',
  profile_update: '更新個人資料',
  mfa_enable: '啟用 MFA',
  mfa_disable: '停用 MFA',
  account_locked: '帳號鎖定（連續輸錯密碼）',
  upload: '上傳',
  download: '下載',
  restore: '還原',
  file_delete: '刪除檔案',
  file_restore: '從回收桶還原',
  file_purge: '永久刪除',
  recycle_bin_empty: '清空回收桶',
  error_files_clear: '清除錯誤檔案',
  backup_execute: '執行備份',
  backup_cancel: '取消備份',
  failed_jobs_clear: '清除失敗記錄',
  schedule_create: '建立排程',
  schedule_update: '修改排程',
  schedule_delete: '刪除排程',
  schedule_trigger: '手動觸發排程',
  user_create: '建立使用者',
  user_update: '修改使用者',
  user_delete: '停用使用者',
  user_purge: '永久刪除使用者',
  // Route removed in QA #75 (2026-09-18); rows before that keep the label.
  user_password_reset: '重設使用者密碼（舊）',
  user_password_setup_email: '寄送設定密碼信',
  user_require_password_change: '要求下次登入更換密碼',
  account_unlock: '解除帳號鎖定',
  password_policy_update: '修改密碼有效期限',
  group_create: '建立群組',
  group_update: '修改群組',
  group_delete: '刪除群組',
  group_member_add: '加入群組成員',
  group_member_remove: '移除群組成員',
  watcher_create: '建立 Watcher',
  watcher_update: '修改 Watcher',
  watcher_delete: '刪除 Watcher',
  watcher_key_regenerate: '重新產生 Watcher 金鑰',
  restore_request_create: '建立還原請求',
  destination_create: '建立備份目的地',
  destination_update: '修改備份目的地',
  destination_delete: '刪除備份目的地',
  destination_test: '測試備份目的地',
  alert_config_create: '建立告警設定',
  alert_config_update: '修改告警設定',
  alert_config_delete: '刪除告警設定',
  smtp_update: '修改 SMTP 設定',
  settings_update: '修改還原設定',
  storage_pool_create: '建立儲存池',
  storage_pool_update: '修改儲存池',
  storage_pool_delete: '刪除儲存池',
  storage_pool_member_add: '加入儲存池成員',
  storage_pool_member_remove: '移除儲存池成員',
};

const AUDIT_RESOURCE_LABEL_MAP: Record<AuditResourceType, string> = {
  session: '登入工作階段',
  user: '使用者',
  group: '群組',
  file: '檔案',
  backup_job: '備份工作',
  schedule: '排程',
  watcher: 'Watcher',
  destination: '備份目的地',
  alert_config: '告警設定',
  smtp: 'SMTP',
  settings: '設定',
  storage_pool: '儲存池',
};

export const AUDIT_RESULT_LABELS: Record<AuditResult, string> = {
  success: '成功',
  failure: '失敗',
};

/**
 * Rows of the 改前 → 改後 table for an audit row whose details carry
 * before / after objects (QA #99: PUT /settings/restore). The labels are the
 * restore page's own wording (settings.service.ts maps the same fields).
 */
export interface AuditChangeRow {
  label: string;
  before: string;
  after: string;
  changed: boolean;
}

const RESTORE_SETTING_FIELDS: { key: string; label: string }[] = [
  { key: 'restore_path', label: '預設還原目錄' },
  { key: 'preferred_watcher', label: '綁定的 File Watcher' },
  { key: 'preserve_permissions', label: '還原時保留原始目錄結構' },
  { key: 'overwrite_existing', label: '如果檔案已存在，自動重新命名' },
  { key: 'create_subfolders', label: '還原時自動建立不存在的子目錄' },
];

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function settingValue(side: Record<string, unknown>, key: string): string {
  if (key === 'preferred_watcher') {
    const id = side.preferred_watcher_id;
    if (typeof id !== 'string' || id === '') return '（未指定）';
    const name = side.preferred_watcher_name;
    return typeof name === 'string' && name !== '' ? name : id;
  }
  const v = side[key];
  if (typeof v === 'boolean') return v ? '是' : '否';
  if (v === undefined || v === null || v === '') return '—';
  return String(v);
}

/** null when the row carries no before / after pair. */
export function auditChangeRows(details: Record<string, unknown> | undefined): AuditChangeRow[] | null {
  const before = details?.before;
  const after = details?.after;
  if (!isRecord(before) || !isRecord(after)) return null;
  return RESTORE_SETTING_FIELDS.map(({ key, label }) => {
    const b = settingValue(before, key);
    const a = settingValue(after, key);
    return { label, before: b, after: a, changed: b !== a };
  });
}

/** Failure reason of a result=failure row, from the details the trail wrote. */
export interface AuditFailure {
  status?: number;
  code?: string;
  message?: string;
  fields: [string, string][];
}

export function auditFailure(details: Record<string, unknown> | undefined): AuditFailure {
  const fields = isRecord(details?.error_fields)
    ? Object.entries(details.error_fields).map(([k, v]): [string, string] => [k, String(v)])
    : [];
  return {
    status: typeof details?.status === 'number' ? details.status : undefined,
    code: typeof details?.error_code === 'string' ? details.error_code : undefined,
    message: typeof details?.error_message === 'string' ? details.error_message : undefined,
    fields,
  };
}

export const AUDIT_ACTION_LABELS: Record<string, string> = AUDIT_ACTION_LABEL_MAP;
export const AUDIT_RESOURCE_LABELS: Record<string, string> = AUDIT_RESOURCE_LABEL_MAP;

export const auditLogService = {
  async list(params: ListAuditLogsParams = {}): Promise<ListAuditLogsResponse> {
    const query: Record<string, string | number> = {};
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== '') query[k] = v;
    }
    const response = await api.get<ApiResponse<ListAuditLogsResponse>>('/api/v1/audit-logs', {
      params: query,
    });
    const data = response.data.data;
    return {
      data: data?.data ?? [],
      pagination: data?.pagination ?? {
        page: params.page ?? 1,
        limit: params.limit ?? 25,
        total: 0,
        total_pages: 0,
      },
    };
  },
};
